import type { TaskDetail, TimeEntry } from '@pm/schemas';
import {
  Avatar,
  Badge,
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  Field,
  Input,
  LabelCaps,
  Skeleton,
  Textarea,
} from '@pm/ui';
import { format } from 'date-fns';
import { Pause, Play, Plus, Trash2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { api, ApiRequestError } from '@/lib/api';
import { useMe } from '@/lib/auth';
import { fmtDateTime, fmtMinutes, str } from '@/lib/format';
import { mgKeys, useInvalidating, useTaskTime } from '@/lib/queries';
import { useToast } from '@/lib/toast';

export const fmtSeconds = (s: number) => {
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  return h > 0
    ? `${h}:${String(m).padStart(2, '0')}:${String(sec).padStart(2, '0')}`
    : `${m}:${String(sec).padStart(2, '0')}`;
};

/** Relógio que anda enquanto o cronômetro roda. */
export function Elapsed({ since, className }: { since: string; className?: string }) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);
  return (
    <span className={className}>
      {fmtSeconds(Math.max(0, Math.floor((now - new Date(since).getTime()) / 1000)))}
    </span>
  );
}

/** SPEC §39 — cronômetro e lançamento manual na tarefa; total deriva dos registros (RULE-058). */
export function TimePanel({ task }: { task: TaskDetail }) {
  const toast = useToast();
  const me = useMe();
  const { data, isLoading } = useTaskTime(task.id);
  const [manual, setManual] = useState(false);
  const inval = [mgKeys.taskTime(task.id), mgKeys.timer, ['task', task.id]];
  const start = useInvalidating(
    () => api.post<TimeEntry>(`/tasks/${task.id}/time/start`, {}),
    inval,
  );
  const stop = useInvalidating(() => api.post<TimeEntry>(`/tasks/${task.id}/time/stop`, {}), inval);
  const remove = useInvalidating((id: string) => api.delete(`/time-entries/${id}`), inval);
  const closed = task.status === 'COMPLETED' || task.status === 'CANCELED';

  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex items-center gap-2">
        <LabelCaps>Tempo</LabelCaps>
        <span className="font-mono text-xs text-text-2">
          {data ? fmtSeconds(data.total_seconds) : '—'}
        </span>
        {!closed && task.can.comment ? (
          <span className="ml-auto flex gap-1">
            {data?.running ? (
              <Button
                variant="secondary"
                size="sm"
                className="h-6 text-xs"
                loading={stop.isPending}
                onClick={() =>
                  stop.mutate(undefined, {
                    onSuccess: () => toast.success('Tempo registrado'),
                    onError: (e) => toast.error(e),
                  })
                }
              >
                <Pause /> <Elapsed since={data.running.started_at} />
              </Button>
            ) : (
              <Button
                variant="ghost"
                size="sm"
                className="h-6 text-xs"
                loading={start.isPending}
                onClick={() =>
                  start.mutate(undefined, {
                    onError: (e) =>
                      toast.error(
                        e instanceof ApiRequestError && e.code === 'TIMER_ALREADY_RUNNING'
                          ? `Você já tem um cronômetro rodando na #${str(e.details?.task_number ?? '?')}.`
                          : e,
                      ),
                  })
                }
              >
                <Play /> Iniciar
              </Button>
            )}
            <Button
              variant="ghost"
              size="icon-sm"
              className="size-6"
              aria-label="Lançar manualmente"
              onClick={() => setManual(true)}
            >
              <Plus />
            </Button>
          </span>
        ) : null}
      </div>
      {isLoading ? (
        <Skeleton className="w-2/3" />
      ) : data && data.entries.length > 0 ? (
        <ul className="flex flex-col gap-1">
          {data.entries.slice(0, 6).map((e) => (
            <li key={e.id} className="flex items-center gap-2 text-xs">
              <Avatar name={e.user.name} size="xs" />
              <span className="font-mono text-text-2">
                {e.running ? (
                  <Elapsed since={e.started_at} className="text-accent" />
                ) : (
                  fmtSeconds(e.duration_seconds ?? 0)
                )}
              </span>
              <span className="truncate text-text-3" title={e.description ?? undefined}>
                {e.description ?? fmtDateTime(e.started_at)}
              </span>
              {e.source === 'MANUAL' ? (
                <Badge tone="neutral" className="h-4 text-[9px]">
                  manual
                </Badge>
              ) : null}
              {!e.running && (e.user.id === me.user.id || task.can.edit) ? (
                <Button
                  variant="ghost"
                  size="icon-sm"
                  className="ml-auto size-5"
                  aria-label="Remover"
                  onClick={() => remove.mutate(e.id, { onError: (err) => toast.error(err) })}
                >
                  <Trash2 />
                </Button>
              ) : null}
            </li>
          ))}
          {data.entries.length > 6 ? (
            <li className="font-mono text-[10px] text-text-3">
              +{data.entries.length - 6} registros
            </li>
          ) : null}
        </ul>
      ) : (
        <span className="text-xs text-text-3">
          Nenhum tempo registrado
          {task.estimated_minutes ? ` · estimado ${fmtMinutes(task.estimated_minutes)}` : ''}.
        </span>
      )}
      <Dialog open={manual} onOpenChange={setManual}>
        <DialogContent title="Lançar tempo" size="sm">
          <ManualForm taskId={task.id} onDone={() => setManual(false)} />
        </DialogContent>
      </Dialog>
    </div>
  );
}

function ManualForm({ taskId, onDone }: { taskId: string; onDone: () => void }) {
  const toast = useToast();
  const [start, setStart] = useState(() =>
    format(new Date(Date.now() - 3_600_000), "yyyy-MM-dd'T'HH:mm"),
  );
  const [minutes, setMinutes] = useState('60');
  const [description, setDescription] = useState('');
  const save = useInvalidating(
    (input: Record<string, unknown>) => api.post<TimeEntry>(`/tasks/${taskId}/time`, input),
    [mgKeys.taskTime(taskId), ['task', taskId]],
  );
  return (
    <form
      className="flex flex-col gap-3"
      onSubmit={(e) => {
        e.preventDefault();
        save.mutate(
          {
            started_at: new Date(start).toISOString(),
            duration_minutes: Number(minutes),
            description: description || null,
          },
          {
            onSuccess: () => (toast.success('Tempo lançado'), onDone()),
            onError: (err) => toast.error(err),
          },
        );
      }}
    >
      <div className="grid gap-3 sm:grid-cols-[1fr_120px]">
        <Field label="Início" htmlFor="mt-start">
          <Input
            id="mt-start"
            type="datetime-local"
            mono
            value={start}
            onChange={(e) => setStart(e.target.value)}
          />
        </Field>
        <Field label="Minutos" htmlFor="mt-min">
          <Input
            id="mt-min"
            type="number"
            min={1}
            max={1440}
            mono
            value={minutes}
            onChange={(e) => setMinutes(e.target.value)}
          />
        </Field>
      </div>
      <Field label="Descrição" htmlFor="mt-desc">
        <Textarea
          id="mt-desc"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          className="min-h-[56px]"
        />
      </Field>
      <DialogFooter>
        <Button variant="ghost" type="button" onClick={onDone}>
          Cancelar
        </Button>
        <Button
          variant="primary"
          type="submit"
          loading={save.isPending}
          disabled={!start || Number(minutes) < 1}
        >
          Lançar
        </Button>
      </DialogFooter>
    </form>
  );
}
